import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  InputAdornment,
  IconButton,
  Box,
} from "@mui/material";
import { Person, Email, LocationOn, Phone, Close } from "@mui/icons-material";
import { Transition } from "../assets/assets";

interface EditUserDialogProps {
  open: boolean;
  onClose: () => void;
  user: {
    id: number;
    name: string;
    address: string;
    password: string;
    email: string;
    phone_number: string;
  } | null;
  onUserUpdated: () => void;
}

const EditUserDialog: React.FC<EditUserDialogProps> = ({
  open,
  onClose,
  user,
  onUserUpdated,
}) => {
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [phone_number, setPhoneNumber] = useState("");
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [isModified, setIsModified] = useState(false); 
  useEffect(() => {
    if (user) {
      setName(user.name);
      setAddress(user.address);
      setEmail(user.email);
      setPhoneNumber(user.phone_number);
      setIsModified(false); 
    }
  }, [user]);

  const validateName = (name: string) => {
    const regex = /^[A-Za-z\s-_]+$/;
    return regex.test(name);
  };

  const validateEmail = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validatePhoneNumber = (phone_number: string) => {
    const regex = /^[0-9][0-9]*$/; // Allows only digits from 1 to 9
    return regex.test(phone_number);
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setIsModified(true); // Mark as modified
    if (!validateName(value)) {
      setNameError("Name can only contain letters, spaces, dashes, and underscores.");
    } else {
      setNameError("");
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    setIsModified(true); // Mark as modified
    if (!validateEmail(value)) {
      setEmailError("Please enter a valid email address.");
    } else {
      setEmailError("");
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Remove any non-numeric characters
    const cleanedValue = value.replace(/[^0-9]/g, ''); // Only keep 1-9
    setPhoneNumber(cleanedValue);
    setIsModified(true); // Mark as modified

    if (!validatePhoneNumber(cleanedValue)) {
      setPhoneNumberError("Phone number can only contain digits from 1 to 9.");
    } else {
      setPhoneNumberError("");
    }
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddress(value);
    setIsModified(true); // Mark as modified
  };

  const handleSubmit = () => {
    if (!user) return;

    // Validate inputs before submission
    if (nameError || emailError || phoneNumberError) {
      return; // Prevent submission if there are errors
    }

    const updatedUser   = {
      name,
      address,
      email,
      phone_number,
    };

    const token: string | null = localStorage.getItem("token");
    if (!token) return;

    fetch(`${process.env.REACT_APP_API_BASE_URL}/user/${user.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": token,
      },
      body: JSON.stringify(updatedUser),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.message) {
          console.error("Error updating user:", data.message);
        } else {
          onUserUpdated();
          onClose();
        }
      })
      .catch((error) => console.error("Error updating user:", error));
  };

  const textFieldStyles = {
    input: { color: "black" },
    label: { color: "black" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "black" },
      "&:hover fieldset": { borderColor: "red" },
      "&.Mui-focused fieldset": { borderColor: "red" },
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          boxShadow: 10,
          borderRadius: 3,
          width: 450,
          maxWidth: '100%',
        },
      }}
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(5px)',
          WebkitBackdropFilter: 'blur(5px)',
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "linear-gradient(144deg, rgba(252, 133, 69, 1) 0%, rgba(253, 29, 29, 1) 50%, rgba(252, 133, 69, 0.87) 100%)",
          px: 2,
          py: 1,
        }}
      >
        <DialogTitle sx={{ color: "white", p: 0 }}>Edit User</DialogTitle>
        <IconButton onClick={onClose} sx={{ color: "white" }}>
          <Close />
        </IconButton>
      </Box>
      <DialogContent>
        {/* Name */}
        <TextField
          margin="dense"
          label="Name"
          type="text"
          fullWidth
          variant="outlined"
          value={name}
          onChange={handleNameChange}
          error={!!nameError}
          helperText={nameError}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Person sx={{ color: "black" }} />
              </InputAdornment>
            ),
          }}
          sx={textFieldStyles}
        />

        {/* Email */}
        <TextField
          margin="dense"
          label="Email"
          type="email"
          fullWidth
          variant="outlined"
          value={email}
          onChange={handleEmailChange}
          error={!!emailError}
          helperText={emailError}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Email sx={{ color: "black" }} />
              </InputAdornment>
            ),
          }}
          sx={textFieldStyles}
        />

        {/* Address */}
        <TextField
          margin="dense"
          label="Address"
          type="text"
          fullWidth
          variant="outlined"
          value={address}
          onChange={handleAddressChange}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <LocationOn sx={{ color: "black" }} />
              </InputAdornment>
            ),
          }}
          sx={textFieldStyles}
        />

        {/* Phone Number */}
        <TextField
          margin="dense"
          label="Phone Number"
          type="tel"
          fullWidth
          variant="outlined"
          value={phone_number}
          onChange={handlePhoneNumberChange}
          error={!!phoneNumberError}
          helperText={phoneNumberError}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Phone sx={{ color: "black" }} />
              </InputAdornment>
            ),
          }}
          sx={textFieldStyles}
        />
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "right",
          pb: 2,
          marginRight: 2
        }}>
        <Button
          onClick={handleSubmit}
          disabled={!isModified} 
          sx={{
            backgroundColor: "red",
            color: "white",
            background: "linear-gradient(144deg, rgba(252, 133, 69, 1) 0%, rgba(253, 29, 29, 1) 50%, rgba(252, 133, 69, 0.87) 100%)",
          }}>
          Update User
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditUserDialog;